import React, { useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import ReactDOM from "react-dom";
import Cookies from "js-cookie";
import SpektrixAPI from "../../utils/SpektrixAPI";
import EventsCarousel from "./EventsCarousel";
import TagsForm from "./TagsForm";
import LoginOrRegister from "./LoginOrRegister";

// Waiting for an user to log in using the form on the component.
window.addEventListener(
    "message",
    function receiveMessageRec(e) {
        if (e.data.action == "user-recommended-login") {
            window.removeEventListener("message", receiveMessageRec);

            const container = document.querySelector(
                `[data-login-recommendations-container]`,
            );

            if (container) {
                const userHasLoggedIn = true;
                const root = createRoot(container);
                root.render(
                    <EventsRecommendations
                        userHasLoggedIn={userHasLoggedIn}
                        rootContainer={container}
                    />,
                );
            }
        }
    },
    false,
);

// User is logged in.
let sessionCookieData = Cookies.get("spektrix_session");
sessionCookieData = sessionCookieData ? JSON.parse(sessionCookieData) : {};

if (sessionCookieData?.customer) {
    const container = document.querySelector(
        `[data-login-recommendations-container]`,
    );

    if (container) {
        const userHasLoggedIn = true;
        const root = createRoot(container);
        root.render(
            <EventsRecommendations
                userHasLoggedIn={userHasLoggedIn}
                rootContainer={container}
            />,
        );
    }
}

function EventsRecommendations({ userHasLoggedIn, rootContainer }) {
    const [user, setUser] = useState({});
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
    const [userTags, setUserTags] = useState([]);
    const [audienceTags, setAudienceTags] = useState([]);
    const [eventsHTML, setEventsHTML] = useState("");
    const [isErrorResponse, setIsErrorResponse] = useState(false);
    const [isEditingPreferences, setIsEditingPreferences] = useState(false);

    const spektrixAPI = new SpektrixAPI();

    const tabRecommendedEvents = document.querySelector(
        `[data-tab-recommended-events]`,
    );

    useEffect(() => {
        const fetchInitialData = async () => {
            const audienceTags = await getAudienceTagsSpektrix();
            const customer = userHasLoggedIn ? await getCustomerSpektrix() : {};

            if (isLoginFailed(customer)) {
                setIsErrorResponse(true);
            } else {
                setIsErrorResponse(false);
            }

            if (audienceTags.length) {
                setAudienceTags(audienceTags);
            }

            if (customer.id) {
                const isLoggedIn = Boolean(customer.id);
                setIsUserLoggedIn(isLoggedIn);
                setUser(isLoggedIn ? customer : {});
            }

            if (audienceTags.length && customer.id) {
                const customerTags = getUserAudienceTags(
                    customer,
                    audienceTags,
                );
                setUserTags(customerTags);
            }
        };
        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchRecommendedEvents = async () => {
            if (userTags.length > 0) {
                const html = await getRecommendedEventsAJAX(userTags);
                if (html !== "No results" && html !== "No shows") {
                    setEventsHTML(html);
                } else {
                    const errorHTML = `<div class=""><p>No shows were found for the selected preferences</p></div>`;
                    setEventsHTML(errorHTML);
                }
            } else {
                setEventsHTML("");
            }
        };
        fetchRecommendedEvents();
        // Only run when userTags changes
    }, [userTags]);

    const isLoginFailed = (response) => {
        const message = response?.[0]?.value;

        if (
            Array.isArray(response) &&
            response.length > 0 &&
            response[0].key === "Message" &&
            response[0].value ===
                "Authorization has been denied for this request."
        ) {
            return true;
        } else {
            return false;
        }
    };

    // useEffect(() => {
    //     if (isUserLoggedIn && eventsHTML && tabRecommendedEvents) {
    //         tabRecommendedEvents.click();
    //     }
    // }, [eventsHTML, isUserLoggedIn]);

    const getAudienceTagsSpektrix = async () => {
        let preferences = [];

        let response = await spektrixAPI?.request("/tag-groups", "GET");

        if (response.length) {
            const audiencePreferences = response.find(
                (item) => item.name === "Audience preferences",
            );

            preferences = audiencePreferences?.tags || [];
        }

        return preferences;
    };

    const getCustomerSpektrix = async () => {
        let response = await spektrixAPI?.request("/customer?$expand=1", "GET");

        return response;
    };

    const getUserAudienceTags = (user, audienceTags) => {
        if (!user.id || !audienceTags.length) {
            return [];
        }

        const uTags = user.tags?.length > 0 ? user.tags : [];

        const idsAudienceTags = new Set(audienceTags.map((item) => item.id));

        const matches = uTags.filter((item) => idsAudienceTags.has(item.id));

        return matches;
    };

    const getRecommendedEventsAJAX = async (customerTags) => {
        try {
            const response = await fetch(ajax_client.ajax_url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: new URLSearchParams({
                    action: "get_recommended_events",
                    nonce: ajax_client.park_nonce,
                    user_tags: JSON.stringify(
                        customerTags.map((item) => item.name),
                    ),
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const resp = await response.text();

            if (resp) {
                return resp;
            } else {
                throw new Error("Invalid response");
            }
        } catch (error) {
            console.error("Error:", error);
            throw error;
        }
    };

    const onTagsSaved = async () => {
        setIsEditingPreferences(false); // Hide form after saving
        // Re-fetch customer and tags after saving
        const updatedCustomer = await getCustomerSpektrix();
        setUser(updatedCustomer);
        const updatedUserTags = getUserAudienceTags(
            updatedCustomer,
            audienceTags,
        );
        setUserTags(updatedUserTags);
        // No need to call getRecommendedEventsAJAX here, useEffect will handle it
    };

    // Render JSX.
    if (isErrorResponse) {
        return <LoginOrRegister />;
    }

    if (!isUserLoggedIn || !audienceTags.length) {
        return (
            <div>
                <p>Loading...</p>
            </div>
        );
    }

    if (isUserLoggedIn && (userTags.length === 0 || isEditingPreferences)) {
        return (
            <TagsForm
                audienceTags={audienceTags}
                userTags={userTags}
                onTagsSaved={onTagsSaved}
            />
        );
    }

    if (isUserLoggedIn && eventsHTML && userTags.length) {
        return (
            <>
                <EventsCarousel
                    html={eventsHTML}
                    setIsEditingPreferences={setIsEditingPreferences}
                />
            </>
        );
    }

    return (
        <div>
            <p>Loading...</p>
        </div>
    );
}

export default EventsRecommendations;
